// @ts-nocheck
import React from 'react';
import type { MovementTrackMode } from '../../utils/movementPoseEngine';
import { POSE_BODY_LABELS_MCQ } from '../../utils/movementPoseEngine';

/** Minh họa nghiêng đầu trái/phải (chế «Nghiêng đầu») */
const HeadSilhouette: React.FC<{ tilt: 'up' | 'down' | 'left' | 'right'; className?: string }> = ({
  tilt,
  className = '',
}) => {
  const rot =
    tilt === 'up' ? -22 : tilt === 'down' ? 22 : tilt === 'left' ? -28 : tilt === 'right' ? 28 : 0;
  return (
    <svg viewBox="0 0 80 100" className={className} aria-hidden>
      <ellipse cx="40" cy="88" rx="22" ry="8" fill="currentColor" className="text-slate-600/40" />
      <g style={{ transform: `rotate(${rot}deg)`, transformOrigin: '40px 48px' }}>
        <ellipse
          cx="40"
          cy="38"
          rx="22"
          ry="26"
          fill="currentColor"
          className="text-amber-200/90"
          stroke="currentColor"
          strokeWidth="2"
        />
        <ellipse cx="34" cy="36" rx="3" ry="4" fill="#0f172a" />
        <ellipse cx="46" cy="36" rx="3" ry="4" fill="#0f172a" />
        <path d="M34 48 Q40 52 46 48" fill="none" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" />
      </g>
    </svg>
  );
};

/** Dáng người 4 tư thế (đơn giản) — khớp POSE_BODY_LABELS_MCQ */
const BodyPoseMini: React.FC<{ variant: 0 | 1 | 2 | 3; className?: string }> = ({ variant, className = '' }) => (
  <svg viewBox="0 0 72 88" className={className} aria-hidden>
    <ellipse cx="36" cy="78" rx="18" ry="6" fill="currentColor" className="text-slate-600/35" />
    {/* Thân */}
    <line x1="36" y1="28" x2="36" y2="58" stroke="currentColor" className="text-cyan-200" strokeWidth="4" strokeLinecap="round" />
    {/* Đầu */}
    <circle cx="36" cy="20" r="10" fill="currentColor" className="text-amber-200/95" stroke="currentColor" strokeWidth="2" />
    {variant === 0 && (
      <>
        <line x1="22" y1="36" x2="8" y2="36" stroke="currentColor" className="text-cyan-200" strokeWidth="3.5" strokeLinecap="round" />
        <line x1="50" y1="36" x2="58" y2="44" stroke="currentColor" className="text-cyan-200" strokeWidth="3" strokeLinecap="round" />
      </>
    )}
    {variant === 1 && (
      <>
        <line x1="50" y1="36" x2="64" y2="36" stroke="currentColor" className="text-cyan-200" strokeWidth="3.5" strokeLinecap="round" />
        <line x1="22" y1="36" x2="14" y2="44" stroke="currentColor" className="text-cyan-200" strokeWidth="3" strokeLinecap="round" />
      </>
    )}
    {variant === 2 && (
      <>
        <line x1="22" y1="36" x2="18" y2="14" stroke="currentColor" className="text-cyan-200" strokeWidth="3.5" strokeLinecap="round" />
        <line x1="50" y1="36" x2="54" y2="14" stroke="currentColor" className="text-cyan-200" strokeWidth="3.5" strokeLinecap="round" />
      </>
    )}
    {variant === 3 && (
      <>
        <path
          d="M 22 38 Q 36 48 50 38"
          fill="none"
          stroke="currentColor"
          className="text-cyan-200"
          strokeWidth="3.5"
          strokeLinecap="round"
        />
        <line x1="26" y1="34" x2="30" y2="42" stroke="currentColor" className="text-cyan-200" strokeWidth="2.5" />
        <line x1="46" y1="34" x2="42" y2="42" stroke="currentColor" className="text-cyan-200" strokeWidth="2.5" />
      </>
    )}
  </svg>
);

const HAND_EMOJI_MCQ = ['👍', '✌️', '🖐️', '☝️'];
const HAND_EMOJI_TF = ['👍', '👎'];

/** Gắn minh họa tư thế ngay trên từng đáp án (gọn, dùng trong nút) */
export const OptionPoseInline: React.FC<{
  slotIndex: number;
  mode: MovementTrackMode;
  isTrueFalse: boolean;
  optionCount: number;
  poseHint: string;
}> = ({ slotIndex, mode, isTrueFalse, optionCount, poseHint }) => {
  if (mode === 'hand') {
    const em = isTrueFalse ? HAND_EMOJI_TF : HAND_EMOJI_MCQ;
    return (
      <span className="inline-flex items-center gap-2 shrink-0">
        <span className="text-xl leading-none drop-shadow-sm">{em[slotIndex] ?? '✋'}</span>
        <span className="text-[9px] font-bold uppercase tracking-tight text-white/70">{poseHint}</span>
      </span>
    );
  }
  if (mode === 'pose') {
    const v = Math.min(slotIndex, 3) as 0 | 1 | 2 | 3;
    if (isTrueFalse || optionCount === 2) {
      const pv = slotIndex === 0 ? 0 : 1;
      return (
        <span className="inline-flex items-center gap-2 shrink-0">
          <BodyPoseMini variant={pv} className="h-11 w-10 text-cyan-100" />
          <span className="text-[9px] font-bold uppercase tracking-tight text-white/70">{poseHint}</span>
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-2 shrink-0">
        <BodyPoseMini variant={v} className="h-11 w-10 text-cyan-100" />
        <span className="text-[9px] font-bold uppercase tracking-tight text-white/70">{poseHint}</span>
      </span>
    );
  }
  if (isTrueFalse || optionCount === 2) {
    const tilt = slotIndex === 0 ? 'left' : 'right';
    return (
      <span className="inline-flex items-center gap-2 shrink-0">
        <HeadSilhouette tilt={tilt} className="h-9 w-7 text-teal-200/90" />
        <span className="text-[9px] font-bold uppercase tracking-tight text-white/70">{poseHint}</span>
      </span>
    );
  }
  if (mode === 'head' && optionCount > 2) {
    if (slotIndex > 1) {
      return (
        <span className="inline-flex items-center gap-2 shrink-0 text-[9px] font-bold text-white/70">
          ⌨️ <span className="uppercase tracking-tight">{poseHint}</span>
        </span>
      );
    }
    const tilt = slotIndex === 0 ? 'left' : 'right';
    return (
      <span className="inline-flex items-center gap-2 shrink-0">
        <HeadSilhouette tilt={tilt} className="h-9 w-7 text-teal-200/90" />
        <span className="text-[9px] font-bold uppercase tracking-tight text-white/70">{poseHint}</span>
      </span>
    );
  }
  if (mode === 'head') {
    const tilt = slotIndex === 0 ? 'left' : 'right';
    return (
      <span className="inline-flex items-center gap-2 shrink-0">
        <HeadSilhouette tilt={tilt} className="h-9 w-7 text-teal-200/90" />
        <span className="text-[9px] font-bold uppercase tracking-tight text-white/70">{poseHint}</span>
      </span>
    );
  }
  return null;
};

/** Mô tả ngắn cho ô đáp án (theo slot & chế độ) */
export function getPoseHintForSlot(
  slotIndex: number,
  mode: MovementTrackMode,
  isTrueFalse: boolean,
  optionCount: number
): string {
  if (mode === 'hand') {
    if (isTrueFalse) return slotIndex === 0 ? 'Ngón cái lên' : 'Ngón cái xuống';
    return ['Ngón cái', 'Chữ V', 'Bàn tay mở', 'Chỉ lên'][slotIndex] ?? '';
  }
  if (mode === 'pose') {
    if (isTrueFalse || optionCount === 2) return slotIndex === 0 ? 'Giang tay trái' : 'Giang tay phải';
    if (optionCount === 3) return ['Giang tay trái', 'Giang tay phải', 'Giơ tay lên cao'][slotIndex] ?? '';
    return POSE_BODY_LABELS_MCQ[slotIndex] ?? '';
  }
  if (mode === 'head') {
    if (isTrueFalse || optionCount === 2) return slotIndex === 0 ? 'Nghiêng trái' : 'Nghiêng phải';
    if (optionCount === 3) return ['Nghiêng trái', 'Nghiêng phải', 'Chọn nút'][slotIndex] ?? '';
    return ['Nghiêng trái', 'Nghiêng phải', 'Chọn nút', 'Chọn nút'][slotIndex] ?? '';
  }
  return '';
}

export function splitLabelsForCamera(
  isTrueFalse: boolean,
  optionCount: number,
  options: string[],
  trackMode?: 'head' | 'pose' | 'hand'
): [string, string] | undefined {
  if (isTrueFalse) return ['Đúng', 'Sai'];
  if (optionCount === 2) {
    const a = String(options[0] ?? '').trim().slice(0, 18) || 'Trái';
    const b = String(options[1] ?? '').trim().slice(0, 18) || 'Phải';
    return [a, b];
  }
  if (trackMode === 'head' && optionCount > 2) {
    const a = String(options[0] ?? '').trim().slice(0, 22) || '…';
    const b = String(options[1] ?? '').trim().slice(0, 22) || '…';
    return [a, b];
  }
  return undefined;
}
